import React from 'react';

import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Box, Chip, GridLegacy as Grid, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import currencyFormatter from '~/utils/currencyFormatter';

import { OtaChartData, ParityAnalysis, RevenueHooks, SeverityLevel } from './types';

function fmtPrice(n: number): string {
  return currencyFormatter('AUD', n, 2);
}

function fmtPriceRounded(v: number): string {
  return currencyFormatter('AUD', v, 0);
}

function fmtShortDate(d: string): string {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
}

const SEVERITY_COLORS: Record<SeverityLevel, string> = {
  in_parity: '#27ae60',
  slight: '#f39c12',
  moderate: '#e67e22',
  severe: '#e74c3c',
  no_data: '#bdc3c7',
  no_comparison: '#95a5a6',
};

const RATING_COLORS: Record<string, 'success' | 'warning' | 'error'> = {
  GOOD: 'success',
  FAIR: 'warning',
  POOR: 'error',
  CRITICAL: 'error',
};

interface Props {
  analysis: ParityAnalysis;
  revenueHooks?: RevenueHooks | null;
}

export default function ParityReport({ analysis, revenueHooks = null }: Props) {
  const a = analysis;

  const overviewData = a.dailyDetail.map((d) => ({
    date: fmtShortDate(d.date),
    [a.leColumn]: d.lePrice,
    'Best Competitor': d.bestCompetitorPrice,
  }));

  const dateRangeCols: GridColDef[] = [
    {
      field: 'dateRange',
      headerName: 'Date Range',
      flex: 2,
      valueGetter: (_, row) => `${row.startDate} — ${row.endDate}`,
    },
    { field: 'numDays', headerName: 'Days', width: 70 },
    { field: 'avgLePrice', headerName: 'Avg LE Price', width: 120, valueFormatter: (v: number) => fmtPrice(v) },
    {
      field: 'avgCompetitorPrice',
      headerName: 'Avg Competitor',
      width: 130,
      valueFormatter: (v: number) => fmtPrice(v),
    },
    { field: 'avgDiffPct', headerName: 'Gap', width: 80, valueFormatter: (v: number) => `${v}%` },
    { field: 'competitor', headerName: 'Competitor', flex: 1 },
    {
      field: 'severity',
      headerName: 'Severity',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: SEVERITY_COLORS[params.value as SeverityLevel],
            color: 'white',
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: 11,
          }}
        />
      ),
    },
  ];

  return (
    <Box>
      {/* Score + Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor:
                  SEVERITY_COLORS[
                    a.parityScore >= 80
                      ? 'in_parity'
                      : a.parityScore >= 60
                      ? 'slight'
                      : a.parityScore >= 40
                      ? 'moderate'
                      : 'severe'
                  ],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                {a.parityScore}%
              </Typography>
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="subtitle2" color="textSecondary">
              Parity Score
            </Typography>
            <Typography variant="h6">
              <Chip label={a.parityRating} color={RATING_COLORS[a.parityRating]} size="small" sx={{ mr: 1 }} />
              {a.inParityCount} of {a.totalDates} comparable dates at parity
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {a.dateRangeStart} to {a.dateRangeEnd} &bull; {a.totalCalendarDays} calendar days &bull;{' '}
              {a.numCompetitors} OTAs tracked
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              AVG PRICE GAP
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {fmtPrice(a.avgPriceDiff)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {a.avgPriceDiffPct}% above competitors
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              WORST COMPETITOR
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {a.worstCompetitor}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Cheapest on {a.competitorCheapestCounts[a.worstCompetitor] ?? 0} dates
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              SEVERE ISSUES
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#e74c3c' }}>
              {a.severityCounts.severe ?? 0}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Dates with &gt;10% gap
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              OUT OF PARITY
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {a.outOfParityCount}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              of {a.totalDates} dates
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Revenue Impact */}
      {revenueHooks && (
        <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #e74c3c' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Revenue Impact — What Parity Is Costing You
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Box sx={{ background: 'linear-gradient(135deg, #fef9f9, #fff5f5)', borderRadius: 2, p: 3 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: '#922b21' }}>
                  Estimated Revenue You're Missing
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#c0392b', mt: 1 }}>
                  ${revenueHooks.estimatedExtraRevenueMonthly}
                </Typography>
                <Typography variant="body2" sx={{ color: '#922b21', mt: 0.5 }}>
                  per month (conservative estimate)
                </Typography>
                <Typography variant="caption" sx={{ color: '#95a5a6', mt: 1, display: 'block' }}>
                  ${revenueHooks.estimatedExtraRevenueAnnual} per year
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ background: '#f8f9fb', borderRadius: 2, p: 3 }}>
                <Typography
                  variant="caption"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: '#7f8c8d', mb: 1.5, display: 'block' }}
                >
                  Your Conversion Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#e74c3c' }}>
                    {revenueHooks.cvrOutOfParity}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    when overpriced
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#27ae60' }}>
                    {revenueHooks.cvrAtParity}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    platform average at parity
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#7f8c8d', mt: 1.5, display: 'block' }}>
                  Fixing parity could increase bookings by ~{revenueHooks.cvrImprovementPct}%
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  SEARCH IMPRESSIONS
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {revenueHooks.totalImpressions}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  last 30 days
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  SHOWN OVERPRICED
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#e74c3c' }}>
                  {revenueHooks.outOfParityPct}%
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {revenueHooks.impressionsOutOfParity} impressions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  CURRENT ORDERS
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {revenueHooks.actualOrders30d}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  last 30 days
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  CURRENT REVENUE
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  ${revenueHooks.actualRevenue30d}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  last 30 days
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Main Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Price Comparison Over Time
        </Typography>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={overviewData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis domain={[a.yMin, a.yMax]} tickFormatter={fmtPriceRounded} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v: number) => fmtPrice(v)} />
            <Legend />
            <Line
              type="monotone"
              dataKey={a.leColumn}
              stroke="#1B2A4A"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="Best Competitor"
              stroke="#e74c3c"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* OTA Sub-Charts */}
      {a.otaCharts.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Price by OTA Channel
          </Typography>
          <Grid container spacing={2}>
            {a.otaCharts.map((ota) => (
              <Grid item xs={6} key={ota.name}>
                <OtaChart ota={ota} leColumn={a.leColumn} yMin={a.yMin} yMax={a.yMax} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Problem Date Ranges */}
      {a.dateRanges.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Problem Date Ranges
          </Typography>
          <DataGrid
            rows={a.dateRanges.map((r, i) => ({ id: i, ...r }))}
            columns={dateRangeCols}
            autoHeight
            hideFooter={a.dateRanges.length <= 25}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            density="compact"
          />
        </Paper>
      )}
    </Box>
  );
}

function OtaChart({ ota, leColumn, yMin, yMax }: { ota: OtaChartData; leColumn: string; yMin: number; yMax: number }) {
  const chartData = ota.data.map((d) => ({
    date: fmtShortDate(d.date),
    [leColumn]: d.lePrice,
    [ota.name]: d.otaPrice,
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {ota.name}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Cheapest on {ota.cheapestCount} dates &bull; Available on {ota.availableCount} of {ota.totalDates} dates
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
          <YAxis domain={[yMin, yMax]} tickFormatter={fmtPriceRounded} tick={{ fontSize: 9 }} />
          <Tooltip formatter={(v: number) => fmtPrice(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey={leColumn}
            stroke="#1B2A4A"
            strokeWidth={1.5}
            dot={false}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey={ota.name}
            stroke={ota.color}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}
